import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, Download, RefreshCw, RotateCcw } from 'lucide-react'
import { models, Report } from 'powerbi-client'
import { PowerBIEmbed } from 'powerbi-client-react'
import * as Sentry from '@sentry/react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  exportPowerBiReport,
  refreshPowerBiEmbedToken,
  type ExportFormat,
  type PowerBiDashboardType,
  type PowerBiEmbedConfig,
} from '@/api/powerbi'
import { toast } from '@/stores/useToastStore'
import { usePowerBiFilterStore } from '@/stores/usePowerBiFilterStore'
import { getErrorMessage } from '@/utils/getErrorMessage'
import { SavedReportFilters } from '@/components/PlanDetail/SavedReportFilters'

const TOKEN_REFRESH_INTERVAL_MS = 50 * 60 * 1000
const TOKEN_REFRESH_RETRY_DELAY_MS = 30 * 1000
const TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000
const BOOKMARK_CAPTURE_DEBOUNCE_MS = 500

// Error emitted by the SDK when the embed token has already expired. Treated
// as recoverable: triggers an immediate token refresh instead of an error overlay.
const TOKEN_EXPIRED_ERROR = 'TokenExpired'

// Known Power BI SDK errors that are non-fatal (report is still usable).
// The SDK throws "invoked filter serialization function without filter" when a
// visual has an incomplete Top-N/Advanced filter — a known SDK bug. Clicks on
// such visuals surface as "visualClickedFailed". Neither should hide the report.
const NON_FATAL_PBI_ERRORS = new Set([
  'visualClickedFailed',
  'invoked filter serialization function without filter',
])

export interface PowerBiSlicerConfig {
  title: string
  target: models.IFilterTarget
  values: (string | number)[]
}

function getEmbedErrorMessage(event?: { detail?: { message?: string } }) {
  const message = event?.detail?.message

  if (typeof message === 'string' && message.trim()) {
    return message
  }

  return 'Failed to render the Power BI dashboard'
}

export interface PowerBiActivePage {
  name: string
  displayName?: string
}

export interface PowerBiSavedFiltersConfig {
  planId: string
  /** Maps the active Power BI page to the report_type saved filters are stored under. */
  resolveReportType: (page: PowerBiActivePage) => string
}

interface PowerBiReportEmbedProps {
  embedConfig: PowerBiEmbedConfig
  reportType: PowerBiDashboardType
  pageName?: string
  filters?: models.IFilter[]
  slicer?: PowerBiSlicerConfig
  showPageNavigation?: boolean
  loadingLabel?: string
  cssClassName?: string
  onRefresh?: () => void
  isFetching?: boolean
  persistenceKey?: string
  reportLabel?: string
  savedFilters?: PowerBiSavedFiltersConfig
}

export function PowerBiReportEmbed({
  embedConfig,
  reportType,
  pageName,
  filters,
  slicer,
  showPageNavigation = true,
  loadingLabel = 'dashboard',
  cssClassName = 'absolute inset-0',
  onRefresh,
  isFetching = false,
  persistenceKey,
  reportLabel,
  savedFilters,
}: PowerBiReportEmbedProps) {
  const [isReportRendered, setIsReportRendered] = useState(false)
  const [embedError, setEmbedError] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  const [activePage, setActivePage] = useState<PowerBiActivePage | null>(null)
  const lastToastMessageRef = useRef<string | null>(null)
  const slicerRef = useRef<PowerBiSlicerConfig | undefined>(slicer)
  const reportRef = useRef<Report | null>(null)
  const isMountedRef = useRef(false)
  const onRefreshRef = useRef(onRefresh)
  const refreshTokenNowRef = useRef<(() => void) | null>(null)
  const embedSpanRef = useRef<Sentry.Span | null>(null)
  const initSpanRef = useRef<Sentry.Span | null>(null)
  const renderSpanRef = useRef<Sentry.Span | null>(null)
  const persistenceKeyRef = useRef<string | undefined>(persistenceKey)
  const bookmarkCaptureTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )
  const hasRestoredBookmarkRef = useRef(false)
  const hasEverRenderedRef = useRef(false)
  const mountTimeRef = useRef<number>(performance.now())
  const lastInteractionTimeRef = useRef<number | null>(null)
  const lastInteractionTypeRef = useRef<string | null>(null)
  const reportTypeRef = useRef(reportType)
  const reportIdRef = useRef(embedConfig.reportId)
  const reportLabelRef = useRef<string | undefined>(reportLabel)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    slicerRef.current = slicer
  }, [slicer])

  useEffect(() => {
    onRefreshRef.current = onRefresh
  }, [onRefresh])

  useEffect(() => {
    persistenceKeyRef.current = persistenceKey
  }, [persistenceKey])

  useEffect(() => {
    reportLabelRef.current = reportLabel
  }, [reportLabel])

  const captureBookmarkNow = useCallback(async () => {
    const report = reportRef.current
    const key = persistenceKeyRef.current
    if (!report || !key) return
    try {
      const bookmark = await report.bookmarksManager.capture({
        allPages: true,
        personalizeVisuals: true,
      })
      if (bookmark.state) {
        usePowerBiFilterStore.getState().setBookmark(key, bookmark.state)
      }
    } catch {
      // capture can fail mid-render; ignore
    }
  }, [])

  useEffect(() => {
    const flush = () => {
      if (bookmarkCaptureTimerRef.current !== null) {
        clearTimeout(bookmarkCaptureTimerRef.current)
        bookmarkCaptureTimerRef.current = null
      }
      void captureBookmarkNow()
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flush()
    }
    window.addEventListener('beforeunload', flush)
    window.addEventListener('pagehide', flush)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      window.removeEventListener('beforeunload', flush)
      window.removeEventListener('pagehide', flush)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      flush()
      if (bookmarkCaptureTimerRef.current !== null) {
        clearTimeout(bookmarkCaptureTimerRef.current)
        bookmarkCaptureTimerRef.current = null
      }
    }
  }, [captureBookmarkNow])

  useEffect(() => {
    setIsReportRendered(false)
    setEmbedError(null)
    hasRestoredBookmarkRef.current = false
    hasEverRenderedRef.current = false
    mountTimeRef.current = performance.now()
    lastInteractionTimeRef.current = null
    lastInteractionTypeRef.current = null
    reportTypeRef.current = reportType
    reportIdRef.current = embedConfig.reportId

    const embedSpan = Sentry.startInactiveSpan({
      name: `PBI Embed: ${reportType}:${embedConfig.reportId}`,
      op: 'ui.pbi_embed',
      forceTransaction: true,
      attributes: {
        'pbi.report_id': embedConfig.reportId,
        'pbi.report_type': reportType,
        ...(pageName ? { 'pbi.page_name': pageName } : {}),
      },
    })
    embedSpanRef.current = embedSpan

    const initSpan = Sentry.withActiveSpan(embedSpan, () =>
      Sentry.startInactiveSpan({
        name: 'PBI Iframe Initialization',
        op: 'ui.pbi.init',
      })
    )
    initSpanRef.current = initSpan

    return () => {
      if (initSpanRef.current) {
        initSpanRef.current.setStatus({ code: 2, message: 'cancelled' })
        initSpanRef.current.end()
        initSpanRef.current = null
      }
      if (renderSpanRef.current) {
        renderSpanRef.current.setStatus({ code: 2, message: 'cancelled' })
        renderSpanRef.current.end()
        renderSpanRef.current = null
      }
      if (embedSpanRef.current) {
        embedSpanRef.current.setStatus({ code: 2, message: 'cancelled' })
        embedSpanRef.current.end()
        embedSpanRef.current = null
      }
    }
  }, [
    embedConfig.accessToken,
    embedConfig.embedUrl,
    embedConfig.reportId,
    reportType,
    pageName,
  ])

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null

    const clearTimer = () => {
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
    }

    const runRefresh = async (retryCount: number): Promise<void> => {
      if (cancelled) return
      try {
        const { accessToken: newToken } = await refreshPowerBiEmbedToken(
          embedConfig.reportId,
          reportType
        )
        if (cancelled) return
        await reportRef.current?.setAccessToken(newToken)
        if (cancelled) return
        clearTimer()
        timer = setTimeout(() => {
          void runRefresh(0)
        }, TOKEN_REFRESH_INTERVAL_MS)
      } catch (err) {
        if (cancelled) return
        if (retryCount === 0) {
          clearTimer()
          timer = setTimeout(() => {
            void runRefresh(1)
          }, TOKEN_REFRESH_RETRY_DELAY_MS)
          return
        }
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to refresh Power BI access token'
        toast.error(message)
        onRefreshRef.current?.()
      }
    }

    refreshTokenNowRef.current = () => {
      clearTimer()
      void runRefresh(0)
    }

    timer = setTimeout(() => {
      void runRefresh(0)
    }, TOKEN_REFRESH_INTERVAL_MS)

    return () => {
      cancelled = true
      clearTimer()
      refreshTokenNowRef.current = null
    }
  }, [
    embedConfig.reportId,
    embedConfig.accessToken,
    embedConfig.embedUrl,
    reportType,
  ])

  // If the cached token is within the expiry buffer when the component mounts
  // (e.g. user navigated away for ~50 min), request a fresh embed config
  // immediately rather than embedding with a nearly-expired token.
  useEffect(() => {
    const msRemaining = new Date(embedConfig.expiry).getTime() - Date.now()
    if (msRemaining < TOKEN_EXPIRY_BUFFER_MS) {
      onRefreshRef.current?.()
    }
  }, [embedConfig.expiry, embedConfig.accessToken])

  const eventHandlers = useMemo(() => {
    const applySlicerFromConfig = async (
      report: Report,
      target: PowerBiSlicerConfig
    ) => {
      try {
        const pages = await report.getPages()
        const activePage = pages.find(p => p.isActive) ?? pages[0]
        if (!activePage) return
        const visuals = await activePage.getVisuals()
        const debugEnabled = import.meta.env.VITE_PBI_DEBUG === 'true'
        if (debugEnabled) {
          ;(window as unknown as { pbi?: Report }).pbi = report
        }

        const targetColumn = target.target as {
          table?: string
          column?: string
        }
        if (debugEnabled) {
          console.group('[PBI slicer debug]')
          console.log(
            'looking for slicer bound to:',
            `${targetColumn.table}.${targetColumn.column}`
          )
          console.table(
            visuals.map(v => ({
              name: v.name,
              type: v.type,
              title: v.title,
            }))
          )
        }
        const slicers = visuals.filter(v => v.type === 'slicer')
        let slicerVisual: (typeof slicers)[number] | undefined
        for (const s of slicers) {
          try {
            const state = await s.getSlicerState()
            const targets = (
              state as {
                targets?: { table?: string; column?: string }[]
              }
            ).targets
            if (debugEnabled) {
              console.log('slicer', s.name, 'targets:', JSON.stringify(targets))
            }
            if (
              targets?.some(
                t =>
                  t.table === targetColumn.table &&
                  t.column === targetColumn.column
              )
            ) {
              slicerVisual = s
              break
            }
          } catch (e) {
            if (debugEnabled) {
              console.warn('getSlicerState failed for', s.name, e)
            }
          }
        }
        if (debugEnabled) {
          console.groupEnd()
        }

        if (!slicerVisual) {
          toast.error(
            `Slicer for ${targetColumn.table}.${targetColumn.column} not found`
          )
          return
        }
        const basicFilter: models.IBasicFilter = {
          $schema: 'http://powerbi.com/product/schema#basic',
          target: target.target,
          operator: 'In',
          values: target.values,
          filterType: models.FilterType.Basic,
        }
        await slicerVisual.setSlicerState({ filters: [basicFilter] })
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to set slicer'
        toast.error(message)
      }
    }

    const restoreBookmark = async (report: Report, key: string) => {
      const store = usePowerBiFilterStore.getState()
      const saved = store.getBookmark(key)
      if (!saved) return
      try {
        await report.bookmarksManager.applyState(saved)
      } catch {
        store.clearBookmark(key)
      }
    }

    const scheduleBookmarkCapture = () => {
      const key = persistenceKeyRef.current
      if (!key) return
      if (bookmarkCaptureTimerRef.current !== null) {
        clearTimeout(bookmarkCaptureTimerRef.current)
      }
      bookmarkCaptureTimerRef.current = setTimeout(() => {
        bookmarkCaptureTimerRef.current = null
        void captureBookmarkNow()
      }, BOOKMARK_CAPTURE_DEBOUNCE_MS)
    }

    return new Map<
      string,
      (event: unknown, embeddedEntity: unknown) => void | Promise<void>
    >([
      [
        'loaded',
        async () => {
          setEmbedError(null)
          if (import.meta.env.VITE_PBI_DEBUG === 'true') {
            console.log(
              `[PBI timing] ⏱ loaded: ${Math.round(performance.now() - mountTimeRef.current)}ms from mount`
            )
          }
          if (initSpanRef.current) {
            initSpanRef.current.end()
            initSpanRef.current = null
          }
          if (embedSpanRef.current && !renderSpanRef.current) {
            renderSpanRef.current = Sentry.withActiveSpan(
              embedSpanRef.current,
              () =>
                Sentry.startInactiveSpan({
                  name: 'PBI Visuals Data Rendering',
                  op: 'ui.pbi.render',
                })
            )
          }
          const report = reportRef.current
          const target = slicerRef.current
          const debugEnabled = import.meta.env.VITE_PBI_DEBUG === 'true'
          if (debugEnabled && report) {
            ;(window as unknown as { pbi?: Report }).pbi = report

            console.group('[PBI debug]')
            console.log('report exposed as window.pbi', report)
            try {
              const pages = await report.getPages()
              const active = pages.find(p => p.isActive) ?? pages[0]
              console.log('active page', active?.name, active?.displayName)
              if (active) {
                const visuals = await active.getVisuals()
                console.table(
                  visuals.map(v => ({
                    name: v.name,
                    type: v.type,
                    title: v.title,
                  }))
                )
                for (const v of visuals) {
                  if (v.type === 'slicer') {
                    try {
                      const state = await v.getSlicerState()
                      console.log('slicer', v.title || v.name, 'state:', state)
                    } catch (e) {
                      console.warn('getSlicerState failed for', v.name, e)
                    }
                  }
                }
              }
              console.log('report filters', await report.getFilters())
            } catch (e) {
              console.warn('debug introspection failed', e)
            }
            console.groupEnd()
          }
          if (report && target) {
            await applySlicerFromConfig(report, target)
          }
        },
      ],
      [
        'dataSelected',
        () => {
          lastInteractionTimeRef.current = performance.now()
          lastInteractionTypeRef.current = 'dataSelected'
          scheduleBookmarkCapture()
        },
      ],
      [
        'filtersApplied',
        () => {
          scheduleBookmarkCapture()
        },
      ],
      [
        'visualRendered',
        () => {
          scheduleBookmarkCapture()
        },
      ],
      [
        'commandTriggered',
        () => {
          scheduleBookmarkCapture()
        },
      ],
      [
        'pageChanged',
        event => {
          const newPage = (
            event as { detail?: { newPage?: PowerBiActivePage } } | undefined
          )?.detail?.newPage
          if (newPage?.name) {
            setActivePage({
              name: newPage.name,
              displayName: newPage.displayName,
            })
          }
          scheduleBookmarkCapture()
        },
      ],
      [
        'selectionChanged',
        () => {
          if (lastInteractionTimeRef.current === null) {
            lastInteractionTimeRef.current = performance.now()
            lastInteractionTypeRef.current = 'selectionChanged'
          }
          scheduleBookmarkCapture()
        },
      ],
      [
        'buttonClicked',
        () => {
          lastInteractionTimeRef.current = performance.now()
          lastInteractionTypeRef.current = 'buttonClicked'
          scheduleBookmarkCapture()
        },
      ],
      [
        'rendered',
        async () => {
          setEmbedError(null)
          setIsReportRendered(true)
          void reportRef.current
            ?.getActivePage()
            .then(page =>
              setActivePage({
                name: page.name,
                displayName: page.displayName,
              })
            )
            .catch(() => {
              // active page is optional; saved filters stay disabled without it
            })
          {
            const now = performance.now()
            if (!hasEverRenderedRef.current) {
              const durationMs = Math.round(now - mountTimeRef.current)
              Sentry.metrics.distribution('pbi_initial_render_ms', durationMs, {
                unit: 'millisecond',
                attributes: {
                  report_type: reportTypeRef.current,
                  report_id: reportIdRef.current,
                  ...(reportLabelRef.current
                    ? { report_label: reportLabelRef.current }
                    : {}),
                },
              })
              if (import.meta.env.VITE_PBI_DEBUG === 'true') {
                console.log(
                  `[PBI timing] ⏱ initial render: ${durationMs}ms from mount`
                )
              }
            } else if (lastInteractionTimeRef.current !== null) {
              const durationMs = Math.round(
                now - lastInteractionTimeRef.current
              )
              Sentry.metrics.distribution(
                'pbi_interaction_render_ms',
                durationMs,
                {
                  unit: 'millisecond',
                  attributes: {
                    report_type: reportTypeRef.current,
                    report_id: reportIdRef.current,
                    ...(reportLabelRef.current
                      ? { report_label: reportLabelRef.current }
                      : {}),
                    interaction: lastInteractionTypeRef.current ?? 'unknown',
                  },
                }
              )
              if (import.meta.env.VITE_PBI_DEBUG === 'true') {
                console.log(
                  `[PBI timing] ⏱ re-render after interaction: ${durationMs}ms`
                )
              }
              lastInteractionTimeRef.current = null
              lastInteractionTypeRef.current = null
            }
          }
          hasEverRenderedRef.current = true
          if (renderSpanRef.current) {
            renderSpanRef.current.end()
            renderSpanRef.current = null
          }
          if (embedSpanRef.current) {
            embedSpanRef.current.setStatus({ code: 1, message: 'ok' })
            embedSpanRef.current.end()
            embedSpanRef.current = null
          }
          if (!hasRestoredBookmarkRef.current) {
            hasRestoredBookmarkRef.current = true
            const report = reportRef.current
            const key = persistenceKeyRef.current
            if (report && key) {
              if (bookmarkCaptureTimerRef.current !== null) {
                clearTimeout(bookmarkCaptureTimerRef.current)
                bookmarkCaptureTimerRef.current = null
              }
              await restoreBookmark(report, key)
            }
          }
        },
      ],
      [
        'error',
        event => {
          const message = getEmbedErrorMessage(
            event as { detail?: { message?: string } } | undefined
          )

          if (NON_FATAL_PBI_ERRORS.has(message)) {
            return
          }

          // TokenExpired is recoverable: trigger the same refresh path as the
          // tokenExpired event instead of showing the error overlay.
          if (message === TOKEN_EXPIRED_ERROR) {
            refreshTokenNowRef.current?.()
            return
          }

          // If the report has already rendered at least once, this is a
          // transient interaction error — don't flash the error overlay.
          if (hasEverRenderedRef.current) {
            if (lastToastMessageRef.current !== message) {
              lastToastMessageRef.current = message
              toast.error(message)
            }
            return
          }

          setEmbedError(message)
          setIsReportRendered(false)

          Sentry.captureMessage(`PBI Embed Error: ${message}`, 'error')

          if (initSpanRef.current) {
            initSpanRef.current.setStatus({
              code: 2,
              message: 'internal_error',
            })
            initSpanRef.current.end()
            initSpanRef.current = null
          }
          if (renderSpanRef.current) {
            renderSpanRef.current.setStatus({
              code: 2,
              message: 'internal_error',
            })
            renderSpanRef.current.end()
            renderSpanRef.current = null
          }
          if (embedSpanRef.current) {
            embedSpanRef.current.setStatus({
              code: 2,
              message: 'internal_error',
            })
            embedSpanRef.current.end()
            embedSpanRef.current = null
          }

          if (lastToastMessageRef.current !== message) {
            lastToastMessageRef.current = message
            toast.error(message)
          }
        },
      ],
      [
        'tokenExpired',
        () => {
          refreshTokenNowRef.current?.()
        },
      ],
    ])
  }, [captureBookmarkNow])

  const handleReload = () => {
    lastToastMessageRef.current = null
    setEmbedError(null)
    setIsReportRendered(false)
    onRefresh?.()
  }

  const getReport = useCallback(() => reportRef.current, [])

  const savedFiltersReportType =
    savedFilters && activePage
      ? savedFilters.resolveReportType(activePage)
      : null

  const handleExport = useCallback(
    async (format: ExportFormat, scope: 'currentPage' | 'wholeReport') => {
      const report = reportRef.current
      if (!report || !isReportRendered || isFetching || isExporting) return
      setIsExporting(true)
      try {
        let bookmarkState: string
        try {
          const bookmark = await report.bookmarksManager.capture({
            allPages: true,
            personalizeVisuals: true,
          })
          if (!bookmark.state) throw new Error('Missing report state')
          bookmarkState = bookmark.state
        } catch {
          throw new Error(
            'Unable to capture the current report state. Please wait for the report to finish loading and try again.'
          )
        }
        let pageName: string | undefined
        if (scope === 'currentPage') {
          try {
            const activePage = await report.getActivePage()
            if (!activePage?.name) throw new Error('Missing active page')
            pageName = activePage?.name
          } catch {
            throw new Error(
              'Unable to identify the current report page. Please try exporting again.'
            )
          }
        }
        // Navigation or token refresh can replace the report during capture.
        if (!isMountedRef.current || reportRef.current !== report) return
        const result = await exportPowerBiReport(
          embedConfig.reportId,
          reportType,
          format,
          bookmarkState,
          pageName,
          filters
        )
        if (!isMountedRef.current || reportRef.current !== report) return
        const url = window.URL.createObjectURL(result.blob)
        const link = document.createElement('a')
        link.href = url
        link.download = result.filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (err) {
        if (isMountedRef.current) {
          toast.error(getErrorMessage(err, 'Failed to export the report'))
        }
      } finally {
        if (isMountedRef.current) setIsExporting(false)
      }
    },
    [
      embedConfig.reportId,
      reportType,
      filters,
      isReportRendered,
      isFetching,
      isExporting,
    ]
  )

  const handleReset = useCallback(async () => {
    if (bookmarkCaptureTimerRef.current !== null) {
      clearTimeout(bookmarkCaptureTimerRef.current)
      bookmarkCaptureTimerRef.current = null
    }
    const key = persistenceKeyRef.current
    if (key) {
      usePowerBiFilterStore.getState().clearBookmark(key)
    }
    const report = reportRef.current
    if (!report) return
    setIsReportRendered(false)
    hasEverRenderedRef.current = false
    hasRestoredBookmarkRef.current = true
    try {
      await report.reload()
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to reset the report'
      toast.error(message)
    }
  }, [])

  return (
    <div className="flex min-h-96 flex-1 flex-col bg-muted/20">
      <div className="flex items-center justify-end gap-1 px-2 py-2">
        {savedFilters && (
          <SavedReportFilters
            planId={savedFilters.planId}
            reportType={savedFiltersReportType}
            getReport={getReport}
            disabled={!isReportRendered || isFetching}
          />
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 cursor-pointer"
              disabled={!isReportRendered || isFetching || isExporting}
              aria-label="Export report"
            >
              {isExporting ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {(
              [
                { format: 'PDF', label: 'Export as PDF' },
                { format: 'PPTX', label: 'Export as PowerPoint' },
              ] as const
            ).map(({ format, label }) => (
              <DropdownMenuSub key={format}>
                <DropdownMenuSubTrigger className="cursor-pointer">
                  {label}
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => handleExport(format, 'currentPage')}
                    >
                      Current page
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => handleExport(format, 'wholeReport')}
                    >
                      Whole report
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 cursor-pointer"
                onClick={handleReset}
                disabled={!isReportRendered || isFetching}
                aria-label="Reset filters, slicers, and other data view changes"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Reset filters, slicers, and other data view changes you&rsquo;ve
              made
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="relative min-h-0 flex-1">
        {(!isReportRendered || isFetching) && !embedError && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3 text-center">
              <RefreshCw className="h-5 w-5 animate-spin text-primary" />
              <div>
                <p className="text-sm font-medium text-foreground">
                  Loading {loadingLabel}
                </p>
                <p className="text-xs text-muted-foreground">
                  This can take a few seconds while the report renders.
                </p>
              </div>
            </div>
          </div>
        )}

        {embedError && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/90 px-6 text-center backdrop-blur-sm">
            <div className="space-y-4">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <AlertCircle className="h-6 w-6" />
              </div>
              <div className="space-y-2">
                <h2 className="text-lg font-semibold text-foreground">
                  Dashboard render failed
                </h2>
                <p className="max-w-xl text-sm text-muted-foreground">
                  {embedError}
                </p>
              </div>
              <div className="flex justify-center">
                <Button
                  type="button"
                  onClick={handleReload}
                  disabled={isFetching}
                >
                  Reload report
                </Button>
              </div>
            </div>
          </div>
        )}

        <PowerBIEmbed
          key={`${embedConfig.reportId}-${embedConfig.accessToken}`}
          embedConfig={{
            type: 'report',
            id: embedConfig.reportId,
            embedUrl: embedConfig.embedUrl,
            accessToken: embedConfig.accessToken,
            tokenType: models.TokenType.Embed,
            pageName,
            ...(filters?.length ? { filters } : {}),
            settings: {
              background: models.BackgroundType.Transparent,
              // Default FitToPage preserves the page aspect ratio and leaves
              // empty bands inside the iframe; FitToWidth fills the container.
              layoutType: models.LayoutType.Custom,
              customLayout: {
                displayOption: models.DisplayOption.FitToWidth,
              },
              panes: {
                filters: {
                  expanded: false,
                  visible: false,
                },
                pageNavigation: {
                  visible: showPageNavigation,
                },
              },
              personalizeVisuals: {
                enabled: true,
                perSession: false,
              },
            } as models.ISettings,
          }}
          eventHandlers={eventHandlers}
          cssClassName={cssClassName}
          getEmbeddedComponent={embeddedReport => {
            reportRef.current = embeddedReport as Report
          }}
        />
      </div>
    </div>
  )
}
